/**
 * DESTASH DENORMALIZATION
 *
 * When a collector toggles an item to "For Destash", copy the seller
 * profile's currency + ships-to fields onto the item so Marketplace
 * queries can filter/render without joining to user docs.
 *
 * Also handles the reverse: when destash is toggled OFF, callers can
 * spread `clearDestashFields()` to explicitly null the three
 * denormalized fields.
 *
 * The Cloud Function `syncDestashOnProfileUpdate` handles the
 * "seller changed their profile after listings were live" case; this
 * module is just for the write-time snapshot.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/** Shape returned by the helpers — spread into a Firestore write. */
export interface DestashDenormalizedFields {
  destashCurrency: string | null;
  destashShipsToCountries: string[] | null;
  destashShipsToWorldwide: boolean | null;
}

/** Read the seller's profile and produce the shape to spread into
 *  the destash-item write. Uses whatever's currently on the user doc;
 *  defaults to USD / no ships-to / not-worldwide if the seller hasn't
 *  filled in their profile yet.
 *
 *  Cached per uid within the session so multiple items being toggled
 *  during one modal action don't each re-read the doc. Cache is a
 *  simple in-memory map; forgotten on page reload — good enough. */
const cache = new Map<string, Promise<DestashDenormalizedFields>>();

export async function buildDestashFields(userId: string): Promise<DestashDenormalizedFields> {
  if (cache.has(userId)) return cache.get(userId)!;
  const promise = (async () => {
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) return defaultFields();
      const data = snap.data();
      return {
        destashCurrency: typeof data.currency === 'string' ? data.currency : 'USD',
        destashShipsToCountries: Array.isArray(data.shipsToCountries)
          ? (data.shipsToCountries as string[])
          : [],
        destashShipsToWorldwide: data.shipsToWorldwide === true,
      };
    } catch (err) {
      console.error('[destashDenormalization] profile read failed:', err);
      return defaultFields();
    }
  })();
  cache.set(userId, promise);
  return promise;
}

/** Explicit null-clear used when a destash toggle turns OFF, so the
 *  fields don't linger with stale data from a previous listing. */
export function clearDestashFields(): DestashDenormalizedFields {
  return {
    destashCurrency: null,
    destashShipsToCountries: null,
    destashShipsToWorldwide: null,
  };
}

function defaultFields(): DestashDenormalizedFields {
  return {
    destashCurrency: 'USD',
    destashShipsToCountries: [],
    destashShipsToWorldwide: false,
  };
}

/** Reset the per-session cache. Callers that mutate the user profile
 *  (Settings save) should call this so the NEXT destash toggle in the
 *  same session sees the fresh values. */
export function invalidateDestashDenormalizationCache(userId?: string) {
  if (userId) cache.delete(userId);
  else cache.clear();
}
